import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, PhoneIncoming, PhoneOutgoing, Clock, Search, Filter } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';
import { StatusBadge } from '../components/ui/StatusBadge';
import { pageTransition, staggerContainer, fadeUpItem } from '../hooks/useAnimation';

interface CallRow {
  id: string;
  customer: string;
  number: string;
  direction: 'inbound' | 'outbound';
  status: 'live' | 'ringing' | 'active' | 'ended';
  agent: string;
  intent: string;
  durationSec: number;
}

const CALLS: CallRow[] = [
  { id: 'VC-2041', customer: 'Jane Smith', number: '+44 7700 900123', direction: 'inbound', status: 'live', agent: 'NetworkAgent', intent: 'Broadband slow', durationSec: 142 },
  { id: 'VC-2040', customer: 'Tom Allen', number: '+44 7700 900088', direction: 'inbound', status: 'live', agent: 'TicketAgent', intent: 'Billing dispute', durationSec: 96 },
  { id: 'VC-2039', customer: 'Priya Patel', number: '+44 7700 900512', direction: 'inbound', status: 'ringing', agent: 'AuthAgent', intent: 'Authenticating', durationSec: 8 },
  { id: 'VC-2038', customer: 'Mark Lewis', number: '+44 7700 900471', direction: 'outbound', status: 'active', agent: 'SupervisorAgent', intent: 'Callback — outage', durationSec: 311 },
  { id: 'VC-2037', customer: 'Sara Khan', number: '+44 7700 900654', direction: 'inbound', status: 'ended', agent: 'NetworkAgent', intent: 'No signal — resolved', durationSec: 205 },
  { id: 'VC-2036', customer: 'Unknown caller', number: '+44 7999 000999', direction: 'inbound', status: 'ended', agent: 'AuthAgent', intent: 'Auth failed — escalated', durationSec: 47 },
];

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function Calls() {
  const [query, setQuery] = useState('');
  const live = CALLS.filter((c) => c.status === 'live' || c.status === 'ringing' || c.status === 'active');
  const rows = CALLS.filter(
    (c) => c.customer.toLowerCase().includes(query.toLowerCase()) || c.id.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <motion.div variants={pageTransition} initial="initial" animate="animate" exit="exit" className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Phone className="w-6 h-6 text-vokentra" />
            Calls
          </h1>
          <p className="text-sm text-text-muted mt-1">Active queue and recent call history</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
            <Search className="w-4 h-4 text-text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search calls..."
              className="bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none w-40"
            />
          </div>
          <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-text-muted hover:text-text-primary">
            <Filter className="w-4 h-4" /> <span className="text-sm">Filter</span>
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <motion.div variants={staggerContainer(0.08)} initial="hidden" animate="visible" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Live now', value: live.length, color: 'text-success' },
          { label: 'In queue', value: 3, color: 'text-warning' },
          { label: 'Resolved today', value: 184, color: 'text-text-primary' },
          { label: 'Avg handle time', value: '3:12', color: 'text-info' },
        ].map((t) => (
          <motion.div key={t.label} variants={fadeUpItem}>
            <GlassCard padding="md">
              <p className="text-xs text-text-muted">{t.label}</p>
              <p className={`text-2xl font-bold tabular-nums mt-1 ${t.color}`}>{t.value}</p>
            </GlassCard>
          </motion.div>
        ))}
      </motion.div>

      {/* Call table */}
      <GlassCard padding="none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-muted border-b border-white/[0.06]">
                <th className="px-4 py-3 font-medium">Call ID</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Dir</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Handling agent</th>
                <th className="px-4 py-3 font-medium">Intent</th>
                <th className="px-4 py-3 font-medium text-right">Duration</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-text-secondary">{c.id}</td>
                  <td className="px-4 py-3">
                    <div className="text-text-primary">{c.customer}</div>
                    <div className="text-[11px] text-text-muted">{c.number}</div>
                  </td>
                  <td className="px-4 py-3">
                    {c.direction === 'inbound' ? (
                      <PhoneIncoming className="w-4 h-4 text-success" />
                    ) : (
                      <PhoneOutgoing className="w-4 h-4 text-info" />
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} size="sm" /></td>
                  <td className="px-4 py-3 text-text-secondary">{c.agent}</td>
                  <td className="px-4 py-3 text-text-muted">{c.intent}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-text-secondary">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3 text-text-muted" />
                      {fmt(c.durationSec)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </motion.div>
  );
}

export default Calls;
